import React, { useEffect, useRef, useState } from 'react';
import './contact-messages-landing.styles.scss';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams } from 'react-router-dom';
import { getUniqueDocumentFromFirestore } from '@/utils/getDataFromDbSimplified';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import Loader from '@/components/loader/loader.comp';

const PURPOSE_LABELS = {
  'quote': 'Enterprise Quote',
  'feature': 'Feature Idea',
  'problem': 'Problem Report',
  'hey': 'Just Saying Hey',
};

const ContactMessageView = () => {

  const { contactId } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [response, setResponse] = useState('');
  const [sending, setSending] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const approveRef = useRef();

  useEffect(() => {
    getContact();
  }, [contactId])

  function getContact() {
    getUniqueDocumentFromFirestore({
      'path': `contact_general/${contactId}`,
      'once': true,
      'callback': (data) => {
        if (!data) {
          setNotFound(true);
          return;
        }
        setContact(data);
      }
    })
  }

  function confirmSend() {
    if (!response.trim()) {
      setAlertMessage('Please write a response before sending.');
      return;
    }
    setConfirmMessage(`Send this response to ${contact.fName} ${contact.lName} ` +
      `(${contact.email})?`);
    approveRef.current = () => sendResponse();
  }

  async function sendResponse() {
    setSending(true);
    // Backend command `sendContactResponse` to be implemented by Adam.
    // Sends the email through Brevo and appends the response to the
    // contact doc's `responses` array + flips status to `responded`.
    const res = await UseCloudFunction(
      'sendContactResponse',
      {
        'contactId': contactId,
        'toEmail': contact.email,
        'toName': `${contact.fName} ${contact.lName}`,
        'appId': contact.appId ?? null,
        'purpose': contact.purpose,
        'responseMessage': response,
      }
    )
    setSending(false);
    if (res.error) {
      setAlertMessage(`Sorry, something went wrong sending that response. ` +
        `(Error: ${res.error})`);
      return;
    }
    setResponse('');
    setAlertMessage('Your response has been sent!');
    getContact();
  }

  if (notFound) {
    return (
      <div className='contact-message-view'>
        <button className='g-button text-only' onClick={() => navigate('..')}>
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Back to Messages</span>
        </button>
        <div className="g-space-2"></div>
        <div className='center-col'>
          <h4>That contact message could not be found.</h4>
        </div>
      </div>
    )
  }

  if (!contact) {
    return (
      <div className='center-col'>
        <div className="g-space-2"></div>
        <Loader />
      </div>
    )
  }

  return (
    <div className='contact-message-view'>
      <button className='g-button text-only' onClick={() => navigate('..')}>
        <FontAwesomeIcon icon={faArrowLeft} />
        <span>Back to Messages</span>
      </button>
      <div className="g-space-1"></div>

      <div className='contact-card'>
        <div className='card-head'>
          <h3>{contact.fName} {contact.lName}</h3>
          <span className={`status-badge ${contact.status ?? 'pending'}`}>
            {contact.status ?? 'pending'}
          </span>
        </div>
        <div className='detail-grid'>
          <div className='detail'>
            <label>Email</label>
            <a href={`mailto:${contact.email}`}>{contact.email}</a>
          </div>
          <div className='detail'>
            <label>Purpose</label>
            <span>{PURPOSE_LABELS[contact.purpose] ?? contact.purpose ?? '—'}</span>
          </div>
          <div className='detail'>
            <label>Submitted</label>
            <span>
              {contact.ts_created ? format(new Date(contact.ts_created), 'PPpp') : '—'}
            </span>
          </div>
          <div className='detail'>
            <label>Registered User</label>
            <span>{contact.appId ? 'Yes' : 'No (guest)'}</span>
          </div>
        </div>
        <div className='detail full'>
          <label>Message</label>
          <p className='message-body'>{contact.message}</p>
        </div>
        {
          (contact.imageURL) &&
          <div className='detail full'>
            <label>Attached Image</label>
            <a href={contact.imageURL} target='_blank' rel='noreferrer'>
              {contact.imageName ?? 'View image'}
            </a>
          </div>
        }
      </div>

      {
        (contact.responses && Object.values(contact.responses).length > 0) &&
        <div className='contact-card'>
          <div className='card-head'>
            <h3>Previous Responses</h3>
          </div>
          {
            Object.values(contact.responses)
              .sort((a, b) => a.ts < b.ts ? 1 : -1)
              .map(r => (
                <div key={r.ts} className='response-item'>
                  <div className='response-meta'>
                    <span>{r.from ?? 'Gamably'}</span>
                    <span>{r.ts ? format(new Date(r.ts), 'Pp') : ''}</span>
                  </div>
                  <p className='message-body'>{r.message}</p>
                </div>
              ))
          }
        </div>
      }

      <div className='contact-card'>
        <div className='card-head'>
          <h3>Send a Response</h3>
        </div>
        <p className='hint'>
          This email is sent to the user through our email provider (Brevo).
        </p>
        <div className='field'>
          <label>Your Response</label>
          <textarea
            rows='6'
            value={response}
            placeholder={`Hi ${contact.fName}, thanks for reaching out...`}
            onChange={e => setResponse(e.target.value)}>
          </textarea>
        </div>
        <div className='buttons'>
          {
            (sending)
              ?
              <button className='g-button primary disabled'>
                <ThreeDotsLoader />
              </button>
              :
              <button className='g-button primary' onClick={() => confirmSend()}>
                Send Response
              </button>
          }
        </div>
      </div>

      <ModalAlert
        show={alertMessage}
        cancel={() => setAlertMessage(null)}
        message={alertMessage}
        full={true} />
      <ModalConfirm
        show={confirmMessage}
        cancel={() => setConfirmMessage(null)}
        message={confirmMessage}
        onApprove={approveRef.current}
        full={true} />
    </div>
  )
}

export default ContactMessageView;
